import * as React from 'react';
import {flow, forEach, get, startsWith} from 'lodash';
import {Button, Col, Form, Row} from 'react-bootstrap';
import {change, formValueSelector, getFormSyncErrors, reduxForm, submit} from 'redux-form';
import {isRequired} from '../../../../util/validators';
import {connect} from 'react-redux';
import {IStore} from '../../../../redux/IStore';
import QuoteSummary from '../QuoteSummary';
import {bindActionCreators} from 'redux';
import {push} from 'react-router-redux';
import {actions as quoteActions, actions} from '../../../../services/quote/reducer';
import {FaSpinner} from 'react-icons/fa';
import {renewalTypeSelectionEnum} from './components/YourRenewal';
import {CommonPayment, validate} from './common';
import {shouldIgnoreCardPermissions} from '../../../../helpers/Quote';
import MedicalImportantSection from '../QuoteSummary/MedicalImportantSection';
import QuoteSummaryHeader from '../QuoteSummary/components/QuoteSummaryHeader';
import {Compliance} from './components/Compliance';
import MarketingOptions from './components/MarketingOptions';
import {PaymentConsent} from '../../../../components/PaymentConsent';
import {BsInfoCircle} from 'react-icons/bs';
import {GooglePayWrapper} from '../../../../components/PayWithWallet';
import {IPaymentWallet, IPaymentWalletProvider} from '../../../../data/api/models';
import ApplePayWrapper from '../../../../components/PayWithWallet/components/ApplePayWrapper';
import FuelShortageWarning from '../../components/FuelShortageWarning';

class PaymentForm extends CommonPayment {

  state = {
    selectedPaymentMethod: 'none',
  };

  private scrollToBottom = () => {
    window.scrollTo(0, document.body.scrollHeight);
  };

  private contactUs = () => {
    window.open('https://www.admiral.com/contact-us/travel', '_blank');
  };

  private handleBack = () => {
    window.history.back();
  };

  private handleGooglePayClick = () => {
    this.handlePaymentMethodClick('google');
    this.props.change('selectedPaymentMethod', 'google');
  };

  private handleApplePayClick = () => {
    this.handlePaymentMethodClick('apple');
    this.props.change('selectedPaymentMethod', 'apple');
  };

  private handleCardClick = () => {
    this.handlePaymentMethodClick('card');
    this.props.change('selectedPaymentMethod', 'card');
  };

  private handlePaymentMethodClick = (method: 'card' | 'google' | 'apple') => {
    this.setState({ selectedPaymentMethod: method });
  };

  private renderGatewayErrorMessage = () => {
    const { quoteReference } = this.props;
    return (
      <Row>
        <Col>
          <p><b>We were unable to process your payment request.</b></p>
          <p>No payment has been taken and no insurance cover is in place.</p>
          <p style={{ margin: '20px 0' }}>
            <b>
              <span onClick={this.scrollToBottom} className={'link-element'}>Click here to try again</span>
            </b>
          </p>
          <p>If you experience any further problems, please <span
            onClick={this.contactUs} className={'link-element'}>contact us</span> so we can help you further.
          </p>
          <p>Your quote details have been saved – quote Ref {quoteReference}</p>
        </Col>
      </Row>
    );
  };

  private renderDeclinePaymentErrorMessage = () => {
    const { quoteReference } = this.props;
    return (
      <Row>
        <Col>
          <p><b>Your payment has been declined by your bank.</b></p>
          <p className={'margin-bottom'}>No payment has been taken and no insurance cover is in place.</p>
          <p><b><span onClick={this.scrollToBottom} className={'link-element'}>Click here</span> to try again or change your card details.</b></p>
          <p>Your quote details have been saved – quote ref: {quoteReference}</p>
        </Col>
      </Row>
    );
  };

  private renderGooglePayButton = () => {
    const {premium, change, paymentWalletProviderConfigData} = this.props;

    if (!paymentWalletProviderConfigData.googlePay.enabled) {
      return null;
    }

    const onGooglePayLoadPaymentData = (paymentData: google.payments.api.PaymentData) => {
      const paymentWallet: IPaymentWallet = {
        provider: IPaymentWalletProvider.GOOGLE_PAY,
        data: paymentData,
      };

      change('paymentWallet', paymentWallet);
      change('permissions', false);
      this.checkForErrors();
    };

    return (
      <div onClick={this.handleGooglePayClick} className="gateway-payment-button google-pay-wrapper">
        <GooglePayWrapper
          grossPremium={premium.gross.toString()}
          onGooglePayLoadPaymentData={onGooglePayLoadPaymentData}
          paymentWalletProviderConfigData={paymentWalletProviderConfigData}
        />
      </div>
    );
  };

  private renderApplePayButton = () => {
    const { premium, change, paymentWalletProviderConfigData} = this.props;

    if (!paymentWalletProviderConfigData.applePay.enabled) {
      return null;
    }

    const onPaymentAuthorized = (
      paymentData: ApplePayJS.ApplePayPayment,
    ): Promise<any> => {

      const paymentWallet: IPaymentWallet = {
        provider: IPaymentWalletProvider.APPLE_PAY,
        data: paymentData,
      };

      change('paymentWallet', paymentWallet);
      change('permissions', false);

      return this.checkForErrors();
    };


    return (
        <ApplePayWrapper
          grossPremium={premium.gross.toString()}
          paymentWalletProviderConfigData={paymentWalletProviderConfigData}
          onPaymentAuthorized={onPaymentAuthorized}
          onClick={this.handleApplePayClick}
        />
    );
  };

  private renderDigitalWalletPaymentOptions = (isInWalletTestGroup) => {
    if (!this.walletsAvailable()) {
      return null;
    }

    return (
      <div className="gateway-payment-container">
        {this.renderGooglePayButton()}
        {this.renderApplePayButton()}

        {isInWalletTestGroup ? (
          <Button
            bsSize="lg"
            className="gateway-payment-button card-button"
            onClick={this.checkForErrors}
          >
            PAY BY CARD
          </Button>
        ) : (
          <Button
            bsSize="lg"
            className="gateway-payment-button card-button"
            onClick={this.handleCardClick}
          >
            Card
          </Button>
        )}
      </div>
    );
  };

  private walletsAvailable = () => {
    const { quote, paymentWalletProviderConfigData } = this.props;
    return quote.application.tripType === 'single'
      && (paymentWalletProviderConfigData.applePay.enabled || paymentWalletProviderConfigData.googlePay.enabled)
      && this.isInWalletTestGroup();
  };

  public render() {
    const {
      product,
      submitting,
      permissions,
      change,
      handleSubmit,
      valid,
      dispatch,
      submitFailed,
      quote,
      password,
      passwordConfirmation,
      errorMessage,
      channel,
      premium,
      travellers,
      renderDefinition,
      quoteType,
      renewalStatus,
      medicalsDeclared,
      billing,
    } = this.props;

    const leadTraveller = quote.application.travellers[0];
    const customer = quote.customer;
    const hasMedical = this.hasTravellerMedical();
    const hasCustomer = !!customer;
    const isIOM = leadTraveller.address && startsWith(leadTraveller.address.postcode, 'IM');

    const disableSubmit = (customer.status === 'new' && password !== passwordConfirmation) || isIOM;

    const formHasError = isRequired && submitFailed && !valid;
    const isAggregator = channel.channelType === 'AGG';

    const referrals = get(premium, 'referrals', []);
    const printOptionsEnabled = quote.application.quoteType !== 'renewal';
    const shouldShowSection = quote.application.tripType === 'annual' && quoteType !== 'renewal';
    const ignorePermissions = shouldIgnoreCardPermissions();

    let fraudMessage = '';
    if (errorMessage && errorMessage.toLowerCase().includes('fraud')) {
      fraudMessage = 'Card Payment was unsuccessful: It appears that you are abroad already. Unfortunately we are unable to accept payments if you are already overseas. If this is not the case please call our customer services team on 0333 234 9913';
    }

    if (!permissions && !ignorePermissions) {
      change('renewalStatus', renewalTypeSelectionEnum.MANUAL);
    }

    if (referrals.length > 0) {
      return (
        <div style={{ textAlign: 'center' }}>
          <h1>Sorry, something went wrong</h1>
          <p>
            Unfortunately there has been a problem with your quote , please see
            our <a className="pointer" href={'https://www.admiral.com/contact-us/travel'} target={'_blank'}>Contact
            Us</a> page for
            details on how to get in touch.
          </p>
        </div>
      );
    }

    return (
      <div className={'payment-page'}>
        <QuoteSummaryHeader
          tripType={this.props.tripType}
          premium={this.props.premium}
          quoteReference={this.props.quoteReference}
          schemeId={this.props.schemeId}
        />

        <Form horizontal={true} onSubmit={handleSubmit} autoComplete="off">

          {(formHasError || errorMessage) && (
            <div className="error-block-container" id="form-errors">
              <h1>Whoops!</h1>

              {formHasError && (
                <p>Missing something? Please check the areas marked in orange before you continue.</p>
              )}

              {errorMessage && fraudMessage && <p>{fraudMessage}</p>}

              {errorMessage && errorMessage !== 'Payment Error' && (
                this.renderGatewayErrorMessage()
              )}

              {errorMessage === 'Payment Error' && (
                this.renderDeclinePaymentErrorMessage()
              )}
            </div>
          )}

          {isIOM && (
            <div className="error-block-container">
              <p>
                {`If you would like to continue a quote or purchase, please contact our call-centre on ${channel.phone}.`}
              </p>
            </div>
          )}

          <div className="steps-container payment-step-container">
            <QuoteSummary
              channel={channel}
              isAggregator={isAggregator}
              product={product}
              quote={quote}
              selectedPremium={premium}
              renderDefinition={renderDefinition}
              quoteType={quoteType}
              permissions={permissions}
              change={change}
              dispatch={dispatch}
              isAnnual={quote.application.tripType === 'annual'}
            />

            <MedicalImportantSection
              hasTravellerDeclaredCondition={hasMedical}
              {...this.props}
            />

            <div className="section-group">
              <div className="quote-card-title">Terms and Conditions</div>
              <div className="section-group-content">
                <Compliance />
                <div className="important-title" style={{ margin: '20px 0' }}>
                  Please make sure you don’t already have cover in place.
                </div>
              </div>
            </div>

            <FuelShortageWarning />

            <MarketingOptions formName={this.props.form} change={change}/>

            {printOptionsEnabled && (
              <div className="section-group">
                <div className="quote-card-title">Policy Document Preferences</div>
                <div className="section-group-content">{this.renderPrintOptions()}</div>
              </div>
            )}
            <div
              className="btn-bar btn-bar--auto-height"
              style={{ display: medicalsDeclared === false ? 'none' : '' }}
            >
              <div className="section-group">
                <div className="quote-card-title">Payment</div>

                <div className="section-group-content">
                  {this.renderTrackingItems()}
                  <div>
                    {medicalsDeclared !== false && (
                      <>
                        {this.renderPremium()}

                        <PaymentConsent
                          key={`payment-consent-card`}
                          change={change}
                          dispatch={dispatch}
                          travellers={travellers}
                          shouldShowRenewalSection={shouldShowSection}
                          channel={channel}
                          ignorePermissions={ignorePermissions}
                          permissions={permissions}
                          billing={billing}
                          renderDefinition={renderDefinition}
                          renewalStatus={renewalStatus}
                          {...this.props}
                        />
                        <div className="card-details-helper">
                          <span><BsInfoCircle /></span> Card details can be entered in the next step
                        </div>

                        {this.renderDigitalWalletPaymentOptions(this.walletsAvailable())}
                      </>
                    )}
                    <div className="payment-buttons">
                      <div className="back-button-wrapper">
                        <Button
                          bsSize="lg"
                          className="back-button"
                          onClick={this.handleBack}
                          disabled={submitting}
                        >
                          BACK
                        </Button>
                      </div>
                      {!this.walletsAvailable() && (
                        <div className="purchase-button-wrapper">
                          <Button
                            type="submit"
                            bsSize="lg"
                            className="purchase-button"
                            onClick={this.checkForErrors}
                            disabled={
                              submitting ||
                              (submitFailed && !valid) ||
                              disableSubmit ||
                              !hasCustomer ||
                              quote.waitForPurchaseToComplete ||
                              (this.walletsAvailable() && this.state.selectedPaymentMethod !== 'card')
                            }
                          >
                            {quote.waitForPurchaseToComplete && (
                              <FaSpinner className="fa-spin" />
                            )}
                            PAY BY CARD
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Form>
      </div>
    );
  }
}

let selector;

export default flow([
  reduxForm({
    form: 'quote',
    validate,
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
  }),
  component => {
    selector = formValueSelector('quote');
    return component;
  },
  connect(
    (state: IStore, ownProps) => {
      const channel = state.branding.channel;
      const quotes = state.quote.quotes;
      const errorMessage = state.quote.errorMessage;
      const synchronousError = getFormSyncErrors('quote')(state);
      const schemeId = selector(state, 'schemeId');

      const dataSelectors = {
        password: selector(state, 'password'),
        passwordConfirmation: selector(state, 'passwordConfirmation'),
        policyDeclaration: selector(state, 'policyDeclaration'),
        paymentInfo: selector(state, 'payment'),
        startDate: selector(state, 'startDate'),
        endDate: selector(state, 'endDate'),
        post: selector(state, 'communications.post'),
        options: selector(state, 'options'),
        visibility: selector(state, 'visibility'),
        payment: selector(state, 'payment'),
        permissions: selector(state, 'permissions'),
        applicationId: selector(state, 'id'),
        quoteReference: selector(state, 'quoteReference'),
        reuseCustomerAddress: selector(state, 'reuseCustomerAddress'),
        travellers: selector(state, 'travellers'),
        quoteType: selector(state, 'quoteType'),
        renewalStatus: selector(state, 'renewalStatus'),
        medicalsDeclared: selector(state, 'medicalsDeclared'),
        tripType: selector(state, 'tripType'),
        billing: selector(state, 'billing'),
      };

      const paymentWalletProviderConfigData =
        state.quote.paymentWalletProviderConfigData;

      let premium = null;

      forEach(quotes, tripTypes => {
        forEach(tripTypes, quotedPremium => {
          if (quotedPremium.scheme.id === schemeId) {
            premium = quotedPremium;
          }
        });
      });

      return {
        ...ownProps,
        ...dataSelectors,
        premium,
        channel,
        schemeId,
        errorMessage,
        synchronousError,
        paymentWalletProviderConfigData,
      };
    },
    (dispatch, ownProps) => ({
      ...ownProps,
      actions: {
        dispatch,
        submit: bindActionCreators(submit, dispatch),
        calculate: bindActionCreators(actions.calculate, dispatch),
        push: bindActionCreators(push, dispatch),
        quote: bindActionCreators({ ...quoteActions }, dispatch),
        change: bindActionCreators(change, dispatch),
      },
    })
  ),
])(PaymentForm);
